/**
 * API Service for AnalizaPrawna Backend
 */

const API_BASE = '/api/v1';

export interface SearchResult {
  id: number;
  judgmentId: number;
  caseNumber: string;
  judgmentDate: string;
  courtType: string;
  similarity: number;
  snippet: string;
  chunkType?: string;
}

export interface SearchResponse {
  query: string;
  results: SearchResult[];
  total: number;
  limit: number;
  offset: number;
  searchType: 'semantic' | 'hybrid' | 'keyword';
  durationMs: number;
}

export interface JudgmentSource {
  code: string;
  judgmentUrl?: string;
  judgmentId?: string;
  publisher?: string;
  publicationDate?: string;
}

export interface JudgmentDetail {
  id: number;
  caseNumber: string;
  judgmentDate: string;
  courtType: string;
  judgmentType: string;
  summary: string;
  textContent: string;
  judges: string[];
  keywords: string[];
  legalBases: string[];
  chunkCount: number;
  createdAt: string;
  source?: JudgmentSource;
}

export interface HealthStatus {
  status: string;
  uptime?: number;
  database?: {
    connected: boolean;
    poolSize: number;
  };
}

// Deep analysis types
export interface DeepAnalysisSession {
  id: string;
  status: string;
  phase: string;
  mode: string;
  documentSize: number;
  tokensTotal: number;
  createdAt: string;
  completedAt?: string;
  durationMs?: number;
  hasReport: boolean;
  hasFeedback: boolean;
  hasEvaluation: boolean;
  isShared?: boolean;
}

export interface DeepAnalysisHistoryResponse {
  sessions: DeepAnalysisSession[];
  total: number;
  limit: number;
  offset: number;
}

export interface FeedbackSubmission {
  overallRating: number;
  relevanceRating?: number;
  completenessRating?: number;
  clarityRating?: number;
  comment?: string;
  helpfulAspects?: string[];
  improvementSuggestions?: string[];
}

export interface FeedbackResponse {
  id: string;
  sessionId: string;
  overallRating: number;
  categoryRatings?: {
    relevance?: number;
    completeness?: number;
    clarity?: number;
  };
  comment?: string;
  helpful?: string[];
  improvements?: string[];
  submittedAt: string;
}

export interface EvaluationResponse {
  id: string;
  sessionId: string;
  overallScore: number;
  qualityGrade: 'A' | 'B' | 'C' | 'D' | 'F';
  scores: {
    legalBasesCoverage: number;
    authorityScore: number;
    recencyScore: number;
    balanceScore: number;
    confidenceScore: number;
    searchEfficiency: number;
  };
  issues: Array<{
    type: string;
    severity: 'low' | 'medium' | 'high';
    description: string;
  }>;
  suggestions: string[];
  metadata: {
    totalJudgmentsAnalyzed: number;
    searchRoundsCompleted: number;
    totalTokensUsed: number;
    legalBasesIdentified: number;
    legalBasesExplored: number;
    supremeCourtPrecedents: number;
  };
  evaluatedAt: string;
}

export interface FeedbackStatsResponse {
  totalFeedback: number;
  avgRating: number;
  ratingDistribution: Record<number, number>;
  avgByCategory: {
    relevance: number;
    completeness: number;
    clarity: number;
  };
  commonHelpful: Array<{ aspect: string; count: number }>;
  commonImprovements: Array<{ improvement: string; count: number }>;
}

// Deep analysis export types
export interface DeepAnalysisTimelineItem {
  type: 'user' | 'assistant' | 'clarification' | 'report';
  content: string;
  timestamp: string;
}

export interface DeepAnalysisExportData {
  sessionId: string;
  createdAt: string;
  originalQuery: string;
  timeline: DeepAnalysisTimelineItem[];
  tokensUsed: number;
}

// Full session types for session restoration
export interface ClarificationQuestion {
  question: string;
  options: Array<{ label: string; description: string }>;
  allowOther: boolean;
}

export interface FullSessionClarificationRequest {
  id: string;
  requestId: string;
  questions: ClarificationQuestion[];
  responses: Record<string, string> | null;
  status: 'pending' | 'responded' | 'expired';
  createdAt: string;
}

export interface FullSessionConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  referencedJudgments: string[];
  tokensUsed: number;
  createdAt: string;
}

export interface DeepAnalysisFullSession {
  id: string;
  userId: string;
  status: 'pending' | 'clarification' | 'exploring' | 'analyzing' | 'synthesizing' | 'complete' | 'error';
  documentText: string;
  documentPreview: string;
  finalReport: string | null;
  tokensUsed: {
    input: number;
    output: number;
    total: number;
  };
  createdAt: string;
  completedAt: string | null;
  clarificationRequests: FullSessionClarificationRequest[];
  conversationMessages: FullSessionConversationMessage[];
}

// Session history item (already used in getDeepAnalysisHistory)
export interface DeepAnalysisHistoryItem {
  id: string;
  status: string;
  phase: string;
  mode: string;
  documentPreview: string;
  tokensTotal: number;
  createdAt: string;
  completedAt?: string;
  durationMs?: number;
  hasReport: boolean;
  isShared?: boolean;
}

class ApiService {
  private apiKey: string | null = null;

  setApiKey(key: string | null): void {
    this.apiKey = key;
  }

  private getAuthToken(): string | null {
    // Get JWT token from localStorage (set by auth service after login)
    return localStorage.getItem('accessToken');
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Prefer JWT token for authenticated users
    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error ${response.status}`);
    }
    return response.json();
  }

  async searchSemantic(
    query: string,
    options: {
      limit?: number;
      offset?: number;
      threshold?: number;
      courtType?: string;
      dateFrom?: string;
      dateTo?: string;
      judgmentType?: string;
      judgeName?: string;
      legalBase?: string;
    } = {}
  ): Promise<SearchResponse> {
    const params = new URLSearchParams({
      q: query,
      limit: String(options.limit ?? 10),
      offset: String(options.offset ?? 0),
    });
    if (options.threshold) {
      params.set('threshold', String(options.threshold));
    }
    if (options.courtType) {
      params.set('courtType', options.courtType);
    }
    if (options.dateFrom) {
      params.set('dateFrom', options.dateFrom);
    }
    if (options.dateTo) {
      params.set('dateTo', options.dateTo);
    }
    if (options.judgmentType) {
      params.set('judgmentType', options.judgmentType);
    }
    if (options.judgeName) {
      params.set('judgeName', options.judgeName);
    }
    if (options.legalBase) {
      params.set('legalBase', options.legalBase);
    }

    const response = await fetch(`${API_BASE}/search/semantic?${params}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<SearchResponse>(response);
  }

  async searchHybrid(
    query: string,
    options: {
      limit?: number;
      offset?: number;
      vectorWeight?: number;
      courtType?: string;
      dateFrom?: string;
      dateTo?: string;
      judgmentType?: string;
      judgeName?: string;
      legalBase?: string;
    } = {}
  ): Promise<SearchResponse> {
    const params = new URLSearchParams({
      q: query,
      limit: String(options.limit ?? 10),
      offset: String(options.offset ?? 0),
    });
    if (options.vectorWeight !== undefined) {
      params.set('vectorWeight', String(options.vectorWeight));
    }
    if (options.courtType) {
      params.set('courtType', options.courtType);
    }
    if (options.dateFrom) {
      params.set('dateFrom', options.dateFrom);
    }
    if (options.dateTo) {
      params.set('dateTo', options.dateTo);
    }
    if (options.judgmentType) {
      params.set('judgmentType', options.judgmentType);
    }
    if (options.judgeName) {
      params.set('judgeName', options.judgeName);
    }
    if (options.legalBase) {
      params.set('legalBase', options.legalBase);
    }

    const response = await fetch(`${API_BASE}/search/hybrid?${params}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<SearchResponse>(response);
  }

  async searchKeyword(
    query: string,
    options: {
      limit?: number;
      offset?: number;
      courtType?: string;
      dateFrom?: string;
      dateTo?: string;
      judgmentType?: string;
      judgeName?: string;
      legalBase?: string;
    } = {}
  ): Promise<SearchResponse> {
    const params = new URLSearchParams({
      q: query,
      limit: String(options.limit ?? 10),
      offset: String(options.offset ?? 0),
    });
    if (options.courtType) {
      params.set('courtType', options.courtType);
    }
    if (options.dateFrom) {
      params.set('dateFrom', options.dateFrom);
    }
    if (options.dateTo) {
      params.set('dateTo', options.dateTo);
    }
    if (options.judgmentType) {
      params.set('judgmentType', options.judgmentType);
    }
    if (options.judgeName) {
      params.set('judgeName', options.judgeName);
    }
    if (options.legalBase) {
      params.set('legalBase', options.legalBase);
    }

    const response = await fetch(`${API_BASE}/search/keyword?${params}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<SearchResponse>(response);
  }

  async getJudgment(id: number): Promise<JudgmentDetail> {
    const response = await fetch(`${API_BASE}/judgments/${id}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<JudgmentDetail>(response);
  }

  async getJudgments(options: { limit?: number; offset?: number } = {}): Promise<{
    results: JudgmentDetail[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const params = new URLSearchParams({
      limit: String(options.limit ?? 10),
      offset: String(options.offset ?? 0),
    });

    const response = await fetch(`${API_BASE}/judgments?${params}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async getHealth(): Promise<HealthStatus> {
    const response = await fetch('/health');
    return this.handleResponse<HealthStatus>(response);
  }

  async rerankResults(
    query: string,
    results: SearchResult[]
  ): Promise<{ query: string; results: SearchResult[]; durationMs: number }> {
    const response = await fetch(`${API_BASE}/search/rerank`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ query, results }),
    });
    return this.handleResponse(response);
  }

  // Deep Analysis methods
  async getDeepAnalysisHistory(options: {
    limit?: number;
    offset?: number;
  } = {}): Promise<DeepAnalysisHistoryResponse> {
    const params = new URLSearchParams({
      limit: String(options.limit ?? 10),
      offset: String(options.offset ?? 0),
    });

    const response = await fetch(`${API_BASE}/analysis/deep/history?${params}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<DeepAnalysisHistoryResponse>(response);
  }

  async getDeepAnalysisSession(sessionId: string): Promise<DeepAnalysisFullSession> {
    const response = await fetch(`${API_BASE}/analysis/deep/${sessionId}/full`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<DeepAnalysisFullSession>(response);
  }

  async submitFeedback(
    sessionId: string,
    feedback: FeedbackSubmission
  ): Promise<FeedbackResponse> {
    const response = await fetch(`${API_BASE}/analysis/deep/${sessionId}/feedback`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(feedback),
    });
    return this.handleResponse<FeedbackResponse>(response);
  }

  async getFeedback(sessionId: string): Promise<FeedbackResponse | null> {
    try {
      const response = await fetch(`${API_BASE}/analysis/deep/${sessionId}/feedback`, {
        headers: this.getHeaders(),
      });
      if (response.status === 404) {
        return null;
      }
      return this.handleResponse<FeedbackResponse>(response);
    } catch {
      return null;
    }
  }

  async getEvaluation(sessionId: string): Promise<EvaluationResponse | null> {
    try {
      const response = await fetch(`${API_BASE}/analysis/deep/${sessionId}/evaluation`, {
        headers: this.getHeaders(),
      });
      if (response.status === 404) {
        return null;
      }
      return this.handleResponse<EvaluationResponse>(response);
    } catch {
      return null;
    }
  }

  async getFeedbackStats(): Promise<FeedbackStatsResponse> {
    const response = await fetch(`${API_BASE}/analysis/feedback/stats`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<FeedbackStatsResponse>(response);
  }

  // Deep analysis export methods
  async exportDeepAnalysisPDF(data: DeepAnalysisExportData): Promise<Blob> {
    const response = await fetch(`${API_BASE}/export/pdf/deep-analysis`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }

    return response.blob();
  }

  async exportDeepAnalysisDOCX(data: DeepAnalysisExportData): Promise<Blob> {
    const response = await fetch(`${API_BASE}/export/docx/deep-analysis`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }

    return response.blob();
  }
}

export const api = new ApiService();
export default api;
